/**
 * ITASC relevance graph — pure, deterministic, config-as-data.
 *
 * Not a graph database (the vocabulary is a few dozen tags — a real graph
 * store would be pure overhead against the cost/footprint rules); this is
 * the same idea implemented as a small in-memory adjacency list: edges from
 * "what's already known about this incident" to "what becomes more (or
 * less) relevant as a result". Selecting "Entrapment" should make rescue and
 * ambulance visibly more relevant; once life is confirmed clear with no
 * entrapment flagged, they become less relevant — but NEVER unavailable
 * (an officer can always select something the graph didn't anticipate; this
 * only reorders the chip pickers, it never hides a tag or blocks a choice).
 *
 * `evaluate()` re-runs against the live ITASC on every render, so relevance
 * updates immediately as the officer toggles tags or confirms life status —
 * there is no persisted state here beyond the ITASC itself.
 */

import type { ActionTag, ThreatTag, SupportTag, Itasc } from './incident.js';
import { sortTagsByPriority } from './incident-type.js';

export interface TagRelevance {
  surfaceThreats: ThreatTag[];
  surfaceActions: ActionTag[];
  surfaceSupport: SupportTag[];
  demoteActions: ActionTag[];
  demoteSupport: SupportTag[];
}

export interface RelevanceEdge {
  id: string;
  /** Why this edge fired — informational, not currently surfaced in the UI, but keeps each edge self-documenting. */
  description: string;
  when: (itasc: Itasc) => boolean;
  /** The threat tag(s) that `when` actually keys off, when it's a simple tag check — purely descriptive
   * metadata for drawing a "cause" link in the graph view; never consulted by `when` itself. Omitted for
   * edges keyed off free text (e.g. the life-reads-clear demote rule) since there's no single tag to point at. */
  sourceTags?: ThreatTag[];
  surfaceThreats?: ThreatTag[];
  surfaceActions?: ActionTag[];
  surfaceSupport?: SupportTag[];
  demoteActions?: ActionTag[];
  demoteSupport?: SupportTag[];
}

const hasThreat = (itasc: Itasc, tag: ThreatTag): boolean => itasc.threats.selected.includes(tag);
const hasAction = (itasc: Itasc, tag: ActionTag): boolean => itasc.actions.selected.includes(tag);
const isOffensiveInternal = (itasc: Itasc): boolean =>
  itasc.actions.strategy === 'offensive-internal' || hasAction(itasc, 'offensive-internal');

/** Free-text life-risk read explicitly clear of injuries — mirrors the `lifeUnresolved` heuristic in insights/rules.ts, just the positive case. Heuristic on free text, not a structured field — only ever softens ordering, never hides a chip. */
function lifeReadsClear(life: string | undefined): boolean {
  if (!life) return false;
  return /no\s+injur|nil\s+injur|uninjured|all\s+clear|none\s+injured|all\s+accounted/i.test(life);
}

export const RELEVANCE_EDGES: RelevanceEdge[] = [
  {
    id: 'entrapment-rescue',
    description: 'Entrapment flagged — extrication and ambulance become directly relevant.',
    when: (i) => hasThreat(i, 'entrapment'),
    sourceTags: ['entrapment'],
    surfaceActions: ['rescue-search', 'extrication'],
    surfaceSupport: ['ambulance', 'frnsw'],
  },
  {
    id: 'no-entrapment-life-clear-demote-rescue',
    description: 'No entrapment and life explicitly reads clear — rescue/extrication and ambulance become less relevant (still selectable).',
    when: (i) => !hasThreat(i, 'entrapment') && lifeReadsClear(i.threats.life),
    demoteActions: ['rescue-search', 'extrication'],
    demoteSupport: ['ambulance'],
  },
  {
    id: 'multiple-vehicles-traffic',
    description: 'Multiple vehicles flagged — traffic control and additional ambulance become directly relevant.',
    when: (i) => hasThreat(i, 'multiple-vehicles'),
    sourceTags: ['multiple-vehicles'],
    surfaceActions: ['traffic-control'],
    surfaceSupport: ['ambulance', 'police'],
  },
  {
    id: 'unknown-substance-hazmat',
    description: 'Substance unknown or a vapour cloud is present — hazmat team, EPA and decontamination become directly relevant.',
    when: (i) => hasThreat(i, 'unknown-substance') || hasThreat(i, 'vapour-cloud'),
    sourceTags: ['unknown-substance', 'vapour-cloud'],
    surfaceActions: ['decontamination'],
    surfaceSupport: ['hazmat-team', 'epa'],
  },
  {
    id: 'powerlines-down-utilities',
    description: 'Powerlines down — utilities support becomes directly relevant.',
    when: (i) => hasThreat(i, 'powerlines-down'),
    sourceTags: ['powerlines-down'],
    surfaceSupport: ['utilities'],
  },
  {
    id: 'rising-water-ses',
    description: 'Rising water flagged — flood-rescue action and SES support become directly relevant.',
    when: (i) => hasThreat(i, 'rising-water'),
    sourceTags: ['rising-water'],
    surfaceActions: ['flood-rescue'],
    surfaceSupport: ['ses'],
  },
  {
    id: 'fuel-spill-hazmat-support',
    description: 'Fuel spill flagged — EPA support becomes more relevant.',
    when: (i) => hasThreat(i, 'fuel-spill'),
    sourceTags: ['fuel-spill'],
    surfaceSupport: ['epa'],
  },
  {
    id: 'fire-behaviour-signs-defensive',
    description: 'Fire behaviour warning signs flagged — defensive strategy becomes more relevant; offensive internal less so.',
    when: (i) => hasThreat(i, 'fire-behaviour-signs'),
    sourceTags: ['fire-behaviour-signs'],
    surfaceActions: ['defensive'],
    demoteActions: ['offensive-internal'],
  },
  {
    id: 'solar-batteries-utilities',
    description: 'Solar/battery storage flagged — utilities (network operator) support becomes directly relevant for isolation confirmation.',
    when: (i) => hasThreat(i, 'solar-batteries'),
    sourceTags: ['solar-batteries'],
    surfaceSupport: ['utilities'],
  },
  {
    id: 'offensive-internal-entry-control',
    description: 'Offensive internal strategy/action selected — BA entry control action and BA operator/cylinder support become directly relevant.',
    when: (i) => isOffensiveInternal(i),
    surfaceActions: ['ba-entry-control'],
    surfaceSupport: ['ba-operators', 'ba-cylinders'],
  },
  {
    id: 'ventilation-establishing-water',
    description: 'Ventilation action selected — establishing water becomes more relevant, nudging toward a coordinated, ready attack line.',
    when: (i) => hasAction(i, 'ventilation'),
    surfaceActions: ['establishing-water'],
  },
  {
    id: 'overhaul-salvage',
    description: 'Overhaul action selected — salvage becomes directly relevant (protecting contents/minimising further water damage alongside overhaul).',
    when: (i) => hasAction(i, 'overhaul'),
    surfaceActions: ['salvage'],
  },
  {
    id: 'collapse-defensive',
    description: 'Structural collapse flagged — defensive strategy becomes more relevant; offensive internal less so.',
    when: (i) => hasThreat(i, 'collapse'),
    sourceTags: ['collapse'],
    surfaceActions: ['defensive'],
    demoteActions: ['offensive-internal'],
  },
  {
    id: 'wind-change-reassess-tactics',
    description: 'Wind change flagged — indirect attack and exposure/asset protection become more relevant; direct attack (on what may become the new head) is demoted.',
    when: (i) => hasThreat(i, 'wind-change'),
    sourceTags: ['wind-change'],
    surfaceActions: ['indirect-attack', 'exposure-protection'],
    demoteActions: ['direct-attack'],
  },
  {
    id: 'ember-spotting-exposure-check',
    description: 'Ember attack or confirmed spot fires — exposures becomes more relevant to check, and additional tanker capacity becomes more relevant.',
    when: (i) => hasThreat(i, 'ember-attack') || hasThreat(i, 'spot-fires'),
    sourceTags: ['ember-attack', 'spot-fires'],
    surfaceThreats: ['exposures'],
    surfaceSupport: ['additional-tanker'],
  },
  {
    id: 'crown-fire-indirect-and-aviation',
    description: 'Crown fire flagged — indirect attack, aircraft support and NPWS/Forestry Corp become more relevant; direct attack on the crown fire head is demoted.',
    when: (i) => hasThreat(i, 'crown-fire'),
    sourceTags: ['crown-fire'],
    surfaceActions: ['indirect-attack'],
    surfaceSupport: ['aircraft', 'npws-forestry'],
    demoteActions: ['direct-attack'],
  },
  {
    id: 'difficult-terrain-raft',
    description: 'Difficult/remote terrain flagged — RAFT support becomes directly relevant.',
    when: (i) => hasThreat(i, 'difficult-terrain'),
    sourceTags: ['difficult-terrain'],
    surfaceSupport: ['raft'],
  },
  {
    id: 'extreme-fire-danger-conservative-posture',
    description: 'Extreme/Catastrophic fire danger flagged — indirect attack and exposure protection become more relevant; direct attack is demoted (still selectable).',
    when: (i) => hasThreat(i, 'extreme-fire-danger'),
    sourceTags: ['extreme-fire-danger'],
    surfaceActions: ['indirect-attack', 'exposure-protection'],
    demoteActions: ['direct-attack'],
  },
  {
    id: 'backburning-selected-duty-officer',
    description: 'Backburning selected — duty officer becomes more relevant (higher-command awareness/sign-off).',
    when: (i) => hasAction(i, 'backburning'),
    surfaceSupport: ['duty-officer'],
  },
  {
    id: 'ev-hv-battery-response',
    description: 'EV / HV battery flagged — HV isolation action and specialist/water support become directly relevant.',
    when: (i) => hasThreat(i, 'ev-hv-battery'),
    sourceTags: ['ev-hv-battery'],
    surfaceActions: ['hv-isolation'],
    surfaceSupport: ['hazmat-team', 'additional-tanker', 'bulk-water'],
  },
  {
    id: 'ev-thermal-runaway-cooling',
    description: 'Thermal runaway flagged — sustained cooling and post-knockdown monitoring actions become directly relevant.',
    when: (i) => hasThreat(i, 'thermal-runaway'),
    sourceTags: ['thermal-runaway'],
    surfaceActions: ['battery-cooling', 'battery-monitoring'],
    surfaceSupport: ['additional-tanker', 'bulk-water'],
  },
  {
    id: 'ev-stranded-energy-runoff',
    description: 'Stranded energy or an HV battery is flagged — EPA becomes more relevant given corrosive/contaminated fireground run-off from battery fires.',
    when: (i) => hasThreat(i, 'stranded-energy') || hasThreat(i, 'ev-hv-battery'),
    sourceTags: ['stranded-energy', 'ev-hv-battery'],
    surfaceSupport: ['epa'],
  },
  {
    id: 'ev-reignition-custody',
    description: 'Reignition risk flagged — ongoing monitoring action and specialist tow/recovery + police (custody/handover) support become directly relevant.',
    when: (i) => hasThreat(i, 'reignition-risk'),
    sourceTags: ['reignition-risk'],
    surfaceActions: ['battery-monitoring'],
    surfaceSupport: ['tow-recovery', 'police'],
  },
];

/** Evaluate every edge against the current ITASC and merge the results (de-duplicated, order-preserving). */
export function computeTagRelevance(itasc: Itasc): TagRelevance {
  const surfaceThreats: ThreatTag[] = [];
  const surfaceActions: ActionTag[] = [];
  const surfaceSupport: SupportTag[] = [];
  const demoteActions: ActionTag[] = [];
  const demoteSupport: SupportTag[] = [];

  const pushUnique = <T>(arr: T[], vals?: T[]) => {
    for (const v of vals ?? []) if (!arr.includes(v)) arr.push(v);
  };

  for (const edge of RELEVANCE_EDGES) {
    if (!edge.when(itasc)) continue;
    pushUnique(surfaceThreats, edge.surfaceThreats);
    pushUnique(surfaceActions, edge.surfaceActions);
    pushUnique(surfaceSupport, edge.surfaceSupport);
    pushUnique(demoteActions, edge.demoteActions);
    pushUnique(demoteSupport, edge.demoteSupport);
  }

  return { surfaceThreats, surfaceActions, surfaceSupport, demoteActions, demoteSupport };
}

/**
 * Combine the static per-incident-type priority order with the live relevance
 * graph's surface/demote signals: priority sets the base order, then
 * surfaced tags are pulled to the front and demoted tags pushed to the back
 * (relative order within each group is preserved from the base list). Never
 * drops a value — an officer can always find and select any tag; this only
 * changes where it sits in the list. If a tag is (surprisingly) in both
 * lists, surfacing wins.
 */
export function orderTags<T extends string>(
  all: readonly T[],
  opts: { priority?: readonly string[]; surface?: readonly T[]; demote?: readonly T[] },
): T[] {
  const base = opts.priority ? sortTagsByPriority(all, opts.priority) : [...all];
  const surfaceSet = new Set(opts.surface ?? []);
  const demoteSet = new Set((opts.demote ?? []).filter((t) => !surfaceSet.has(t)));

  const front = base.filter((t) => surfaceSet.has(t));
  const back = base.filter((t) => demoteSet.has(t));
  const middle = base.filter((t) => !surfaceSet.has(t) && !demoteSet.has(t));
  return [...front, ...middle, ...back];
}
